import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type {
  NarrativeRule,
  NarrativeRuleInput,
  RecurringObject,
  RecurringObjectInput,
  SensorySignature,
  SensorySignatureInput,
  StoryElements,
  StoryElementsCreationInput,
  StoryElementsStats
} from '@/types';
import { narrativeRuleService } from '@/services/narrativeRuleService';
import { recurringObjectService } from '@/services/recurringObjectService';
import { sensorySignatureService } from '@/services/sensorySignatureService';
import { storyElementsService } from '@/services/storyElementsService';
import { generateId } from '@/utils/storyElementsHelpers';
import { createEntityCache } from '@/stores/_shared/entityStoreHelpers';

interface PersistedState {
  storyElements: StoryElements | null;
}

const cloneStoryElements = (storyElements: StoryElements): StoryElements => ({
  ...storyElements,
  recurringObjects: storyElements.recurringObjects.map((object) => ({
    ...object,
    uiState: { ...object.uiState }
  })),
  sensorySignatures: storyElements.sensorySignatures.map((signature) => ({
    ...signature,
    uiState: { ...signature.uiState }
  })),
  narrativeRules: storyElements.narrativeRules.map((rule) => ({
    ...rule,
    actIds: rule.actIds ? [...rule.actIds] : undefined,
    characterIds: rule.characterIds ? [...rule.characterIds] : undefined,
    uiState: { ...rule.uiState }
  })),
  uiState: { ...storyElements.uiState }
});

const ensureId = (value: string | null): string => {
  if (!value) {
    throw new Error('Active manuscript is required before interacting with story elements.');
  }
  return value;
};

const toRecurringObjectInput = (object: RecurringObject): RecurringObjectInput => ({
  storyElementsId: object.storyElementsId,
  name: object.name,
  description: object.description,
  significance: object.significance,
  category: object.category,
  importance: object.importance,
  firstAppearance: object.firstAppearance,
  lastSeen: object.lastSeen,
  currentLocation: object.currentLocation,
  status: object.status,
  characterId: object.characterId,
  locationId: object.locationId,
  notes: object.notes
});

const toSensorySignatureInput = (signature: SensorySignature): SensorySignatureInput => ({
  storyElementsId: signature.storyElementsId,
  name: signature.name,
  description: signature.description,
  scope: signature.scope,
  scopeId: signature.scopeId,
  scopeName: signature.scopeName,
  trigger: signature.trigger,
  frequency: signature.frequency,
  notes: signature.notes
});

const toNarrativeRuleInput = (rule: NarrativeRule): NarrativeRuleInput => ({
  storyElementsId: rule.storyElementsId,
  rule: rule.rule,
  rationale: rule.rationale,
  severity: rule.severity,
  appliesTo: rule.appliesTo,
  actIds: rule.actIds,
  characterIds: rule.characterIds,
  goodExample: rule.goodExample,
  badExample: rule.badExample,
  notes: rule.notes
});

export const useStoryElementsStore = defineStore('storyElements', () => {
const storyElements = ref<StoryElements | null>(null);
const activeManuscriptId = ref<string | null>(null);
const isLoading = ref(false);
const errorMessage = ref<string | null>(null);
const cache = createEntityCache<StoryElements | null>();

const snapshot = computed<StoryElements | null>(() => {
  const cached = cache.get('active');
  if (cached !== undefined) {
    return cached ? cloneStoryElements(cached) : null;
  }

  const value = storyElements.value ? cloneStoryElements(storyElements.value) : null;
  cache.set('active', value);
  return value ? cloneStoryElements(value) : null;
});

const stats = computed<StoryElementsStats | null>(() => {
  if (!snapshot.value) {
    return null;
  }
  return storyElementsService.ensureStats(snapshot.value);
});

const hasStoryElements = computed(() => snapshot.value !== null);

const recurringObjects = computed<RecurringObject[]>(() => snapshot.value?.recurringObjects ?? []);
const sensorySignatures = computed<SensorySignature[]>(() => snapshot.value?.sensorySignatures ?? []);
const narrativeRules = computed<NarrativeRule[]>(() => snapshot.value?.narrativeRules ?? []);

  const setActiveManuscript = (manuscriptId: string | null): void => {
    activeManuscriptId.value = manuscriptId;
  };

  const loadFromStorage = (): void => {
    const manuscriptId = activeManuscriptId.value;
    if (!manuscriptId) {
      storyElements.value = null;
      return;
    }
    const adapter = storyElementsService.storage(manuscriptId);
    const persisted = adapter.read();
    storyElements.value = persisted ? cloneStoryElements(persisted) : null;
    cache.invalidate('active');
  };

  const persistToStorage = (): void => {
    const manuscriptId = activeManuscriptId.value;
    if (!manuscriptId) {
      return;
    }
    const adapter = storyElementsService.storage(manuscriptId);
    if (!storyElements.value) {
      adapter.clear();
      return;
    }
    cache.invalidate('active');
    adapter.write(cloneStoryElements(storyElements.value));
  };

  const clearState = (): void => {
    storyElements.value = null;
    errorMessage.value = null;
  };

  const createStoryElements = async (input?: Partial<StoryElementsCreationInput>): Promise<StoryElements> => {
    const manuscriptId = ensureId(activeManuscriptId.value);
    isLoading.value = true;
    errorMessage.value = null;
    try {
      const creationInput: StoryElementsCreationInput = {
        manuscriptId,
        notes: input?.notes
      };
    const created = storyElementsService.create(creationInput);
    storyElements.value = cloneStoryElements(created);
    cache.invalidate('active');
    persistToStorage();
    return created;
    } catch (error) {
      errorMessage.value = error instanceof Error ? error.message : 'Failed to create story elements.';
      throw error;
    } finally {
      isLoading.value = false;
    }
  };

  const upsertStoryElements = (next: StoryElements): void => {
  storyElements.value = cloneStoryElements(next);
  cache.invalidate('active');
  persistToStorage();
  };

  const updateStoryElements = async (updates: Partial<StoryElements>): Promise<void> => {
    if (!storyElements.value) {
      throw new Error('No story elements found to update.');
    }
    storyElements.value = {
      ...storyElements.value,
      ...updates,
      updatedAt: new Date().toISOString()
    };
    cache.invalidate('active');
    persistToStorage();
  };

  const addRecurringObject = async (input: RecurringObjectInput): Promise<RecurringObject> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before adding recurring objects.');
    }
    const validation = recurringObjectService.validate(input);
    if (!validation.valid) {
      throw new Error(validation.errors.join(' '));
    }
    const object = recurringObjectService.create(input);
    storyElements.value.recurringObjects.push(object);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
    return object;
  };

  const updateRecurringObject = async (
    objectId: string,
    updates: Partial<RecurringObject>
  ): Promise<RecurringObject> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before updating recurring objects.');
    }
    const index = storyElements.value.recurringObjects.findIndex((item) => item.id === objectId);
    if (index === -1) {
      throw new Error('Recurring object not found.');
    }
    const original = storyElements.value.recurringObjects[index];
    const merged: RecurringObject = {
      ...original,
      ...updates,
      uiState: { ...original.uiState },
      updatedAt: new Date().toISOString()
    };

    const validation = recurringObjectService.validate(toRecurringObjectInput(merged));
    if (!validation.valid) {
      throw new Error(validation.errors.join(' '));
    }

    storyElements.value.recurringObjects.splice(index, 1, merged);
    storyElements.value.updatedAt = merged.updatedAt;
    cache.invalidate('active');
    persistToStorage();
    return merged;
  };

  const removeRecurringObject = async (objectId: string): Promise<void> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before deleting recurring objects.');
    }
    const index = storyElements.value.recurringObjects.findIndex((item) => item.id === objectId);
    if (index === -1) {
      throw new Error('Recurring object not found.');
    }
    storyElements.value.recurringObjects.splice(index, 1);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
  };

  const duplicateRecurringObject = async (objectId: string): Promise<RecurringObject> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before duplicating recurring objects.');
    }
    const original = storyElements.value.recurringObjects.find((item) => item.id === objectId);
    if (!original) {
      throw new Error('Recurring object not found.');
    }
    const duplicateInput: RecurringObjectInput = {
      ...toRecurringObjectInput(original),
      name: `${original.name} (Copy)`,
      status: original.status,
      notes: original.notes
    };

    const duplicate = recurringObjectService.create(duplicateInput);
    storyElements.value.recurringObjects.push(duplicate);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
    return duplicate;
  };

  const addSensorySignature = async (input: SensorySignatureInput): Promise<SensorySignature> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before adding sensory signatures.');
    }
    const validation = sensorySignatureService.validate(input);
    if (!validation.valid) {
      throw new Error(validation.errors.join(' '));
    }
    const signature = sensorySignatureService.create(input);
    storyElements.value.sensorySignatures.push(signature);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
    return signature;
  };

  const updateSensorySignature = async (
    signatureId: string,
    updates: Partial<SensorySignature>
  ): Promise<SensorySignature> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before updating sensory signatures.');
    }
    const index = storyElements.value.sensorySignatures.findIndex((item) => item.id === signatureId);
    if (index === -1) {
      console.error('Sensory signature not found during update.', { signatureId });
      throw new Error('Sensory signature not found.');
    }
    const original = storyElements.value.sensorySignatures[index];
    const merged: SensorySignature = {
      ...original,
      ...updates,
      uiState: { ...original.uiState },
      updatedAt: new Date().toISOString()
    };

    const validation = sensorySignatureService.validate(toSensorySignatureInput(merged));
    if (!validation.valid) {
      console.error('Sensory signature validation failed during update.', {
        signatureId,
        errors: validation.errors
      });
      throw new Error(validation.errors.join(' '));
    }

    storyElements.value.sensorySignatures.splice(index, 1, merged);
    storyElements.value.updatedAt = merged.updatedAt;
    cache.invalidate('active');
    persistToStorage();
    return merged;
  };

  const removeSensorySignature = async (signatureId: string): Promise<void> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before deleting sensory signatures.');
    }
    const index = storyElements.value.sensorySignatures.findIndex((item) => item.id === signatureId);
    if (index === -1) {
      console.error('Sensory signature not found during removal.', { signatureId });
      throw new Error('Sensory signature not found.');
    }
    storyElements.value.sensorySignatures.splice(index, 1);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
  };

  const duplicateSensorySignature = async (signatureId: string): Promise<SensorySignature> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before duplicating sensory signatures.');
    }
    const original = storyElements.value.sensorySignatures.find((item) => item.id === signatureId);
    if (!original) {
      throw new Error('Sensory signature not found.');
    }
    const duplicateInput: SensorySignatureInput = {
      ...toSensorySignatureInput(original),
      name: `${original.name} (Copy)`
    };

    const duplicate = sensorySignatureService.create(duplicateInput);
    storyElements.value.sensorySignatures.push(duplicate);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
    return duplicate;
  };

  const getGlobalSignatures = (): SensorySignature[] => {
    return sensorySignatures.value.filter((signature) => signature.scope === 'global');
  };

  const getSignaturesForLocation = (locationId: string): SensorySignature[] => {
    return sensorySignatures.value.filter(
      (signature) => signature.scope === 'location' && signature.scopeId === locationId
    );
  };

  const getSignaturesForCharacter = (characterId: string): SensorySignature[] => {
    return sensorySignatures.value.filter(
      (signature) => signature.scope === 'character' && signature.scopeId === characterId
    );
  };

  const getSignaturesForAct = (actId: string): SensorySignature[] => {
    return sensorySignatures.value.filter(
      (signature) => signature.scope === 'act' && signature.scopeId === actId
    );
  };

  const updateNarrativeRule = async (
    ruleId: string,
    updates: Partial<NarrativeRule>
  ): Promise<NarrativeRule> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before updating narrative rules.');
    }
    const index = storyElements.value.narrativeRules.findIndex((item) => item.id === ruleId);
    if (index === -1) {
      throw new Error('Narrative rule not found.');
    }
    const original = storyElements.value.narrativeRules[index];
    const merged: NarrativeRule = {
      ...original,
      ...updates,
      actIds: updates.actIds ?? original.actIds,
      characterIds: updates.characterIds ?? original.characterIds,
      uiState: {
        ...original.uiState,
        ...(updates.uiState ?? {})
      },
      updatedAt: new Date().toISOString()
    };

    const validation = narrativeRuleService.validate(toNarrativeRuleInput(merged));
    if (!validation.valid) {
      throw new Error(validation.errors.join(' '));
    }

    storyElements.value.narrativeRules.splice(index, 1, merged);
    storyElements.value.updatedAt = merged.updatedAt;
    cache.invalidate('active');
    persistToStorage();
    return merged;
  };

  const removeNarrativeRule = async (ruleId: string): Promise<void> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before deleting narrative rules.');
    }
    const index = storyElements.value.narrativeRules.findIndex((item) => item.id === ruleId);
    if (index === -1) {
      throw new Error('Narrative rule not found.');
    }
    storyElements.value.narrativeRules.splice(index, 1);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
  };

  const duplicateNarrativeRule = async (ruleId: string): Promise<NarrativeRule> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before duplicating narrative rules.');
    }
    const original = storyElements.value.narrativeRules.find((item) => item.id === ruleId);
    if (!original) {
      throw new Error('Narrative rule not found.');
    }
    const duplicateInput: NarrativeRuleInput = {
      ...toNarrativeRuleInput(original),
      rule: `${original.rule} (Copy)`
    };

    const duplicate = narrativeRuleService.create(duplicateInput);
    storyElements.value.narrativeRules.push(duplicate);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
    return duplicate;
  };

  const addNarrativeRule = async (input: NarrativeRuleInput): Promise<NarrativeRule> => {
    if (!storyElements.value) {
      throw new Error('Create story elements before adding narrative rules.');
    }
    const validation = narrativeRuleService.validate(input);
    if (!validation.valid) {
      throw new Error(validation.errors.join(' '));
    }
    const rule = narrativeRuleService.create(input);
    storyElements.value.narrativeRules.push(rule);
    storyElements.value.updatedAt = new Date().toISOString();
    cache.invalidate('active');
    persistToStorage();
    return rule;
  };

  const getGlobalRules = (): NarrativeRule[] => {
    return narrativeRules.value.filter((rule) => rule.appliesTo === 'all-chapters');
  };

  const getRulesForAct = (actId: string): NarrativeRule[] => {
    return narrativeRules.value.filter(
      (rule) => rule.appliesTo === 'specific-acts' && rule.actIds?.includes(actId)
    );
  };

  const getRulesForCharacter = (characterId: string): NarrativeRule[] => {
    return narrativeRules.value.filter(
      (rule) => rule.appliesTo === 'specific-characters' && rule.characterIds?.includes(characterId)
    );
  };

  const exportStoryElements = (): string => {
    if (!storyElements.value) {
      throw new Error('No story elements available to export.');
    }
    return JSON.stringify(
      {
        version: '1.0.0',
        exportedAt: new Date().toISOString(),
        storyElements: cloneStoryElements(storyElements.value)
      },
      null,
      2
    );
  };

  const importStoryElements = async (jsonData: string): Promise<void> => {
    const manuscriptId = ensureId(activeManuscriptId.value);

    try {
      const parsed = JSON.parse(jsonData);
      const payload = parsed.storyElements ?? parsed;
      if (!payload || typeof payload !== 'object') {
        throw new Error('Invalid story elements payload.');
      }

      const now = new Date().toISOString();
      const storyElementsId = generateId('story-elements');

      const imported: StoryElements = {
        id: storyElementsId,
        manuscriptId,
        uiState: {
          lastActiveTab: (payload.uiState?.lastActiveTab as StoryElements['uiState']['lastActiveTab']) ?? 'objects'
        },
        notes: payload.notes ?? '',
        createdAt: payload.createdAt ?? now,
        updatedAt: now,
        recurringObjects: (payload.recurringObjects ?? []).map((object: Partial<RecurringObject>) => ({
          id: object.id ?? generateId('object'),
          storyElementsId,
          name: object.name ?? '',
          description: object.description ?? '',
          significance: object.significance ?? '',
          category: object.category ?? 'other',
          importance: object.importance ?? 'major',
          firstAppearance: object.firstAppearance ?? '',
          lastSeen: object.lastSeen ?? '',
          currentLocation: object.currentLocation ?? '',
          status: object.status ?? 'active',
          characterId: object.characterId,
          locationId: object.locationId,
          uiState: {
            isExpanded: object.uiState?.isExpanded ?? false
          },
          createdAt: object.createdAt ?? now,
          updatedAt: now,
          notes: object.notes ?? ''
        })),
        sensorySignatures: (payload.sensorySignatures ?? []).map((signature: Partial<SensorySignature>) => ({
          id: signature.id ?? generateId('signature'),
          storyElementsId,
          name: signature.name ?? '',
          description: signature.description ?? '',
          scope: signature.scope ?? 'global',
          scopeId: signature.scopeId,
          scopeName: signature.scopeName,
          trigger: signature.trigger ?? '',
          frequency: signature.frequency ?? 'usually',
          uiState: {
            isExpanded: signature.uiState?.isExpanded ?? false
          },
          createdAt: signature.createdAt ?? now,
          updatedAt: now,
          notes: signature.notes ?? ''
        })),
        narrativeRules: (payload.narrativeRules ?? []).map((rule: Partial<NarrativeRule>) => ({
          id: rule.id ?? generateId('rule'),
          storyElementsId,
          rule: rule.rule ?? '',
          rationale: rule.rationale ?? '',
          severity: rule.severity ?? 'strong-guideline',
          appliesTo: rule.appliesTo ?? 'all-chapters',
          actIds: rule.actIds ?? [],
          characterIds: rule.characterIds ?? [],
          goodExample: rule.goodExample,
          badExample: rule.badExample,
          uiState: {
            isExpanded: rule.uiState?.isExpanded ?? false
          },
          createdAt: rule.createdAt ?? now,
          updatedAt: now,
          notes: rule.notes ?? ''
        }))
      };

      storyElements.value = cloneStoryElements(imported);
      cache.invalidate('active');
      persistToStorage();
    } catch (error) {
      if (error instanceof SyntaxError) {
        throw new Error('Unable to parse import file.');
      }
      throw error instanceof Error ? error : new Error('Failed to import story elements.');
    }
  };

  watch(
    activeManuscriptId,
    () => {
      loadFromStorage();
    },
    { immediate: true }
  );

  return {
    storyElements,
    activeManuscriptId,
    isLoading,
    errorMessage,
    stats,
    hasStoryElements,
    recurringObjects,
    sensorySignatures,
    narrativeRules,
    setActiveManuscript,
    loadFromStorage,
    persistToStorage,
    clearState,
    createStoryElements,
    upsertStoryElements,
    updateStoryElements,
    addRecurringObject,
    updateRecurringObject,
    removeRecurringObject,
    duplicateRecurringObject,
    addSensorySignature,
    updateSensorySignature,
    removeSensorySignature,
    duplicateSensorySignature,
    getGlobalSignatures,
    getSignaturesForLocation,
    getSignaturesForCharacter,
    getSignaturesForAct,
    addNarrativeRule,
    updateNarrativeRule,
    removeNarrativeRule,
    duplicateNarrativeRule,
    getGlobalRules,
    getRulesForAct,
    getRulesForCharacter,
    exportStoryElements,
    importStoryElements
  };
});
